import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';

import { ApiError } from '@/lib/api/client';
import { useStackRecovery } from '@/lib/api/stackHooks';
import { track } from '@/lib/analytics';
import type { MarketplaceProduct } from '@/types/api';
import { stackRecoveryCartItems } from '../stack';
import { cart } from '../stores/cart';
import { cartUi } from '../stores/cartUi';

export const STACK_RECOVERY_PARAM = 'stack_recovery';

const RETRY_NOTICE =
  "We couldn't open your payment link right now. Reload the page to try again.";

/**
 * The emailed pay link for a stack product whose payment failed after the
 * other product was paid: `/app/shop?stack_recovery=<slug>`. An open recovery
 * puts the listed product in the cart at its stack share and opens the cart,
 * where it is bought with the recovery. A paid, expired or unknown link says
 * so here instead, and the param is consumed. A lookup or Shop load that
 * failed keeps the param, so a reload tries again. The same link opened again
 * later is looked up afresh.
 */
export function StackRecoveryLanding({
  products,
  shopFailed = false,
}: {
  /** The Shop's products, once loaded. */
  products: MarketplaceProduct[] | undefined;
  /** The Shop's products could not be loaded. */
  shopFailed?: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const slug = searchParams.get(STACK_RECOVERY_PARAM);
  const [notice, setNotice] = useState<string | null>(null);
  const consume = useCallback(
    () =>
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(STACK_RECOVERY_PARAM);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  return (
    <>
      {slug && (
        <RecoveryLink
          key={slug}
          slug={slug}
          products={products}
          shopFailed={shopFailed}
          onNotice={setNotice}
          onDone={consume}
        />
      )}
      {notice && (
        <div
          role="status"
          className="mx-15 mt-13 flex items-start justify-between gap-10 border border-line-subtle bg-sunken px-12 py-12"
        >
          <p className="font-body text-14 leading-135 text-ink">{notice}</p>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setNotice(null)}
            className="shrink-0 min-h-44 min-w-44 -my-12 -mr-12 inline-flex items-center justify-center text-ink-secondary hover:text-ink"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
      )}
    </>
  );
}

/** One opened link: looked up, handled once, then the param is consumed. */
function RecoveryLink({
  slug,
  products,
  shopFailed,
  onNotice,
  onDone,
}: {
  slug: string;
  products: MarketplaceProduct[] | undefined;
  shopFailed: boolean;
  onNotice: (notice: string | null) => void;
  onDone: () => void;
}) {
  const recovery = useStackRecovery(slug);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current || recovery.isPending) return;
    const data = recovery.data;
    const notFound =
      recovery.error instanceof ApiError && recovery.error.status === 404;
    if (
      (recovery.isError && !notFound) ||
      (data?.status === 'open' && !products && shopFailed)
    ) {
      handled.current = true;
      onNotice(RETRY_NOTICE);
      return;
    }
    if (data?.status === 'open' && !products) return;
    handled.current = true;
    const lines =
      data?.status === 'open' ? stackRecoveryCartItems(data, products ?? []) : null;
    // A link that can no longer be paid takes its stale line out of the cart.
    if (data?.status === 'paid' || data?.status === 'expired')
      cart
        .getSnapshot()
        .items.filter((item) => item.stack_recovery === slug)
        .forEach((item) => cart.remove(item.id));
    if (lines) {
      onNotice(null);
      cart.addStack(lines);
      cartUi.open();
    } else if (data?.status === 'paid') {
      onNotice(`${data.stack_title} is fully paid. There's nothing more to pay.`);
    } else if (data?.status === 'expired') {
      onNotice(
        'This payment link has expired, so the stack price no longer applies. You can still add the treatment at its single price.',
      );
    } else {
      onNotice(
        "We couldn't open that payment link. Message our team if you still need to pay for your stack.",
      );
    }
    track('marketplace_stack_recovery_opened', {
      stack_id: data?.stack_id ?? null,
      status: data?.status ?? 'not_found',
      added: Boolean(lines),
    });
    onDone();
  }, [
    slug,
    recovery.isPending,
    recovery.isError,
    recovery.data,
    recovery.error,
    products,
    shopFailed,
    onNotice,
    onDone,
  ]);

  return null;
}
